import * as F from "fp-ts/function"
import { Lazy } from "fp-ts/function"
import * as TE from "fp-ts/TaskEither"
import { TaskEither } from "fp-ts/TaskEither"
import * as T from "fp-ts/Task"
import { Task } from "fp-ts/Task"
import * as E from "fp-ts/Either"
import { Either } from "fp-ts/Either"
import { apFirst, apSecond } from "fp-ts/Apply"
import * as GenericError from "./generic-error"
import { toError } from "./error"

export const lazyOf =
    <E = never, A = never>(a: A) =>
    (): TE.TaskEither<E, A> =>
        TE.of(a)

export const mapTo =
    <N extends string, A, E, B>(name: Exclude<N, keyof A>, f: (a: A) => B) =>
    (ma: TE.TaskEither<E, A>): TE.TaskEither<E, { readonly [K in N | keyof A]: K extends keyof A ? A[K] : B }> =>
        F.pipe(
            ma,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            TE.map((a) => ({ ...a, [name]: f(a) }) as any),
        )

type BindEither<N extends string, A, E, B> = (
    ma: TaskEither<E, A>,
) => TaskEither<E, { readonly [K in N | keyof A]: K extends keyof A ? A[K] : B }>
export const bindEither = <N extends string, A, E, B>(
    name: Exclude<N, keyof A>,
    f: (a: A) => Either<E, B>,
): BindEither<N, A, E, B> => TE.bind(name, TE.fromEitherK(f))

type BindEitherW<N extends string, A, E2, B> = <E1>(fa: TaskEither<E1, A>) => TaskEither<
    E1 | E2,
    {
        readonly [K in keyof A | N]: K extends keyof A ? A[K] : B
    }
>
export const bindEitherW = <N extends string, A, E2, B>(
    name: Exclude<N, keyof A>,
    f: (a: A) => Either<E2, B>,
): BindEitherW<N, A, E2, B> => TE.bindW(name, TE.fromEitherK(f))

export const bindRight =
    <N extends string, A, E, B>(name: Exclude<N, keyof A>, f: (a: A) => B) =>
    (ma: TaskEither<E, A>): TaskEither<E, { readonly [K in keyof A | N]: K extends keyof A ? A[K] : B }> =>
        F.pipe(
            ma,
            TE.bind(
                name,
                F.flow(f, (b) => TE.of(b)),
            ),
        )

export const apFirstSeq = /* @__PURE__ */ apFirst(TE.ApplySeq)

export const apSecondSeq = /* @__PURE__ */ apSecond(TE.ApplySeq)

export const tryCatchError = <A>(f: Lazy<Promise<A>>): TaskEither<Error, A> => TE.tryCatch(f, toError)

export const tryCatchGenericError = <A>(f: Lazy<Promise<A>>, message: string) =>
    F.pipe(TE.tryCatch(f, toError), TE.mapLeft(GenericError.fromError(message)))

export const tryCatchGenericErrorK = <A extends readonly unknown[], B>(
    f: (...a: A) => Promise<B>,
    message: string,
): ((...a: A) => TaskEither<GenericError.GenericError, B>) =>
    F.flow(TE.tryCatchK(f, toError), TE.mapLeft(GenericError.fromError(message)))

export const ignoreError: <E>(ma: TaskEither<E, void>) => Task<void> = /* @__PURE__ */ TE.getOrElse(() =>
    T.fromIO(F.constVoid),
)

type Cacheable1Fn<E, A, B> = (a: A) => TaskEither<E, B>
export const cacheFunction1 = <Err, A, B>(
    keyFor: (x: A) => string,
    f: Cacheable1Fn<Err, A, B>,
): Cacheable1Fn<Err, A, B> => {
    const cache: { [key: string]: Promise<Either<Err, B>> } = {}
    return (a: A) => {
        const key = keyFor(a)
        return async () => {
            if (!cache[key]) cache[key] = f(a)()
            const result = await cache[key]
            if (E.isLeft(result)) delete cache[key]
            return result
        }
    }
}

type Cacheable0Fn<E, B> = () => TaskEither<E, B>
export const cacheFunction0 = <Err, B>(f: Cacheable0Fn<Err, B>): Cacheable0Fn<Err, B> => {
    let cache: Promise<Either<Err, B>> | null = null
    return () => {
        return async () => {
            if (!cache) cache = f()()
            const result = await cache
            if (E.isLeft(result)) cache = null
            return result
        }
    }
}
